import React, { FC, memo, useCallback, useMemo, useRef, useState } from 'react'
import classes from './TableMainForMarix.module.scss'
import ScrollButtonHorizontal from '../../uikit/atoms/buttons/ScrollButtonHorizontal'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Button, useMediaQuery, useTheme, useThemeProps } from '@mui/material'
import { useRecoilState, useRecoilValue } from 'recoil'
import { userState } from '../../../states/userState'
import { User } from '../../../types'
import { setUserToLocalStorage } from '../../../utils/functions/localStorageManager'
import PushPinIcon from '@mui/icons-material/PushPin'
import { MdModeEdit } from 'react-icons/md'
import {
  Calendar,
  Dirary,
  processInfo,
  summaryByProductNo,
  summaryByWorker,
} from '../../../types/dirary'
import ViewComment from '../../modal/Input/ViewComment'
import { IconContext } from 'react-icons'
import SingleInputModal from '../../diaryList/displayArea/Modal/SingleInputModal'
import { zoomState } from '../../../states/zoomState'
import { calcTotalTimeByDiary } from '../../../utils/functions/calcTotalTime'
import { formatWorkerId } from '../../../utils/variables/workerId'

type Props = {
  processList: processInfo[]
  diaryList: Dirary[]
  calendar: Calendar[]
  summaryByWorker: summaryByWorker
  summaryByProductNo: summaryByProductNo
}
const createOnedayDiary = (
  targetDate: string,
  diaryList: Dirary[],
  processList: processInfo[]
) => {
  const filteredDiaries = diaryList.filter(
    (diary) => diary.workingDate === targetDate
  )
  if (filteredDiaries.length === 0) {
    return null
  }
  const totalDirectLaborByHour = filteredDiaries
    .filter((diary) =>
      processList.some(
        (process) =>
          Number(process.processId) === Number(diary.processId) &&
          Number(process.laborClassification) === 1
      )
    )
    .reduce((total, diary) => total + diary.workingTimeByHour, 0)
  const { endTime } = calcTotalTimeByDiary(
    filteredDiaries[0].startTime,
    filteredDiaries,
    filteredDiaries[0].isBreak,
    filteredDiaries[0].breakTime
  )

  const totalWorkingTimeByHour = filteredDiaries.reduce(
    (total, diary) => total + diary.workingTimeByHour,
    0
  )

  return {
    workerId: filteredDiaries[0].workerId,
    workerName: filteredDiaries[0].workerName,
    workingDate: targetDate,
    startTime: filteredDiaries[0].startTime,
    endTime: filteredDiaries[0].endTime,
    isBreak: filteredDiaries[0].isBreak,
    breakTime: filteredDiaries[0].breakTime
      ? Math.round((filteredDiaries[0].breakTime / 60) * 10) / 10
      : 0.0,

    totalWorkingTimeByHour: totalWorkingTimeByHour,
    totalDirectLaborByHour,
  }
}
// const filterDiary = (diaryList: Dirary[], summary: summaryByWorker) => {
//   const filteredDiaryList = diaryList.filter(
//     (diary) =>
//       diary.workerId === summary.workerId &&
//       diary.constructionId === summary.constructionId &&
//       diary.productNo === summary.productNo &&
//       diary.productBno === summary.productBno
//   );
//   return filteredDiaryList;
// };
const filterDiary = (
  diaryList: Dirary[],
  summary: summaryByWorker
): Dirary[] => {
  // workerId + workingDate ごとにグループ化
  const diaryMap = new Map<string, Dirary[]>()

  diaryList.forEach((diary) => {
    const key = `${diary.workerId}_${diary.workingDate}`
    if (!diaryMap.has(key)) {
      diaryMap.set(key, [])
    }
    diaryMap.get(key)?.push(diary)
  })

  // endTime を決定してセットする
  const withEndTime: Dirary[] = []

  diaryMap.forEach((group) => {
    const first = group[0]

    // すでにendTimeが入っている（nullでもundefinedでも空文字でもない）かを確認
    const existingEndTime = group.find(
      (diary) =>
        diary.endTime !== null &&
        diary.endTime !== undefined &&
        diary.endTime !== ''
    )?.endTime

    const endTimeToUse =
      existingEndTime ??
      calcTotalTimeByDiary(
        first.startTime,
        group,
        first.isBreak,
        first.breakTime
      ).endTime

    group.forEach((diary) => {
      withEndTime.push({
        ...diary,
        endTime: endTimeToUse,
      })
    })
  })

  // 最後に constructionId / productNo / productBno / factoryId でフィルタ
  return withEndTime.filter(
    (diary) =>
      diary.workerId === summary.workerId &&
      diary.constructionId === summary.constructionId &&
      diary.productNo === summary.productNo &&
      diary.productBno === summary.productBno &&
      diary.factoryId === summary.factoryId
  )
}

const TableMainForMatrix: FC<Props> = memo(
  ({
    processList,
    diaryList,
    summaryByWorker,
    summaryByProductNo,
    calendar,
  }) => {
    const zoomLevel = useRecoilValue(zoomState)
    const zoom = parseFloat(zoomLevel) || 1
    const [user, setUser] = useRecoilState<User>(userState)
    const [comment, setComment] = useState<string>('')
    const [workerId, setWorkerId] = useState<string>('')
    const [dateString, setDateString] = useState<string>('')
    const [isShowCommentModal, setIsShowCommentModal] = useState<boolean>(false)
    const [isShowSingleInputModal, setIsShowSingleInputModal] =
      useState<boolean>(false)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isIphone = useMediaQuery(theme.breakpoints.down('sm'))
    const isNotePc = useMediaQuery(theme.breakpoints.down('xl'))

    const targetRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const filteredDiaryList = filterDiary(diaryList, summaryByWorker)
    const [isPinned, setIsPinned] = useState<boolean>(
      user.initialParameters.isPinned
    )
    const filteredProcessList = processList.filter(
      (process: processInfo) =>
        process.productNo === summaryByWorker.productNo &&
        process.productBno === summaryByWorker.productBno
    )
    // const dateArray = generateDates(startDate, endDate);
    const handleSlipNumberChange = () => {
      setUser((prevUser: User) => {
        const newUser: User = {
          ...prevUser,
          initialParameters: {
            ...prevUser.initialParameters,
            isShowSlipNumber: !prevUser.initialParameters.isShowSlipNumber,
          },
        }
        setUserToLocalStorage(newUser)
        return newUser
      })
    }

    //マウスアクション
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const handleMouseDown = (e: React.MouseEvent) => {
      setIsDragging(true)
      setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0))
      setScrollLeft(scrollRef.current?.scrollLeft || 0)
    }

    const handleMouseLeave = () => {
      setIsDragging(false)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    const handleMouseMove = (e: React.MouseEvent) => {
      if (!isDragging) return
      e.preventDefault()
      const x = e.pageX - (scrollRef.current?.offsetLeft || 0)
      const walk = (x - startX) * 2 // スクロールの速さを調整
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollLeft - walk
      }
    }
    const handleClickCells = (comment: string) => {
      if (comment.length === 0) return
      setComment(comment)
      setIsShowCommentModal(true)
    }

    const headerWidth = () => {
      return user.initialParameters.isShowSlipNumber ? '300px' : '150px'
    }
    const contentWidth = () => {
      // console.log(
      //   "isMobile",
      //   isMobile,
      //   "isIphone",
      //   isIphone,
      //   user.initialParameters.isShowSlipNumber
      // );
      if (isIphone) {
        return user.initialParameters.isShowSlipNumber
          ? `calc((100% - 240px * ${zoom}) /${zoom})`
          : `calc((100% - 150px * ${zoom}) /${zoom})`
        // `calc((100vw - 10px * ${zoom}) / ${zoom})
      }
      if (isMobile) {
        return user.initialParameters.isShowSlipNumber
          ? `calc((100% - 240px * ${zoom}) /${zoom})`
          : `calc((100% - 152px * ${zoom}) /${zoom})`
      }
      if (isNotePc) {
        return user.initialParameters.isShowSlipNumber
          ? `calc((100% - 200px * ${zoom}) /${zoom})`
          : `calc((100% - 160px * ${zoom}) /${zoom})`
      }
      return user.initialParameters.isShowSlipNumber
        ? `calc((100% - 100px * ${zoom}) /${zoom})`
        : `calc((100% - 100px * ${zoom}) /${zoom})`
    }
    const createCellStyle = (
      isDisabled: boolean,
      totalWorkingTime: number,
      comments: string
    ) => {
      const style = {
        backgroundColor:
          !isDisabled && totalWorkingTime > 0
            ? theme.palette.foreColor.light
            : 'inherit',
        boxShadow:
          !isDisabled && totalWorkingTime > 0
            ? '4px 4px 6px rgba(0, 0, 0, 0.5)'
            : 'none', // ここでboxShadowを追加
        textDecoration: comments.length > 0 ? 'underline' : 'none',
        color: comments.length > 0 ? theme.palette.foreColor.error : 'inherit',
        cursor: comments.length > 0 ? 'pointer' : 'inherit',
      }
      return style
    }
    const handleEditClick = useCallback(
      (date: string) => {
        setDateString(date)
        setWorkerId(formatWorkerId(summaryByWorker.workerId))
        setIsShowSingleInputModal(true)
      },
      [summaryByWorker]
    )
    const handlePinnedChange = () => {
      setIsPinned(!isPinned)
      setUser((prevUser: User) => {
        const newUser: User = {
          ...prevUser,
          initialParameters: {
            ...prevUser.initialParameters,
            isPinned: !isPinned,
          },
        }
        setUserToLocalStorage(newUser)
        return newUser
      })
    }

    return (
      <div
        className={classes.rootContainer}
        ref={!isPinned ? scrollRef : undefined}
        onMouseDown={!isPinned ? handleMouseDown : undefined}
        onMouseLeave={!isPinned ? handleMouseLeave : undefined}
        onMouseUp={!isPinned ? handleMouseUp : undefined}
        onMouseMove={!isPinned ? handleMouseMove : undefined}
        style={
          !isPinned
            ? {
                maxWidth: isIphone
                  ? `calc((100vw - 10px) / ${zoom})`
                  : isMobile
                  ? `calc((100vw - 10px) / ${zoom})`
                  : `calc((100vw - 20px) / ${zoom})`,
              }
            : {}
        }
      >
        <div
          className={classes.tableContainer}
          style={
            isPinned
              ? {
                  maxWidth: isIphone
                    ? `calc((100vw - 10px) / ${zoom})`
                    : isMobile
                    ? `calc((100vw - 10px) / ${zoom})`
                    : `calc((100vw - 40px) / ${zoom})`,
                }
              : {}
          }
        >
          <div
            className={classes.headers}
            style={{
              minWidth: headerWidth(),
            }}
          >
            <div>
              <div
                className={classes.pinIconContainer}
                onClick={handlePinnedChange}
                style={{
                  color: isPinned
                    ? theme.palette.primary.main
                    : theme.palette.grey[500],
                  cursor: 'pointer',
                }}
              >
                {`固定 ${isPinned ? 'ON' : 'OFF'}`}
                <PushPinIcon
                  style={{
                    transform: isPinned ? 'rotate(0deg)' : 'rotate(-90deg)',
                    color: isPinned
                      ? theme.palette.primary.main
                      : theme.palette.grey[500],
                    marginLeft: '5px',
                  }}
                />
              </div>
            </div>
            <div
              className={classes.workingDay}
              onClick={handleSlipNumberChange}
            >
              <p>作業日</p>
              <div>
                <p
                  style={{
                    color: !user.initialParameters.isShowSlipNumber
                      ? theme.palette.backColor.main
                      : 'inherit',
                  }}
                >
                  詳細
                </p>
                {user.initialParameters.isShowSlipNumber ? (
                  <VisibilityIcon
                    sx={{
                      color: theme.palette.foreColor.light,
                      width: '20px',
                    }}
                  />
                ) : (
                  <VisibilityOffIcon
                    sx={{ color: theme.palette.backColor.main, width: '20px' }}
                  />
                )}
              </div>
            </div>
            <div>開始時刻</div>
            <div>終了時刻</div>
            <div>休憩時間</div>
            <div>作業時間（直接）</div>
            <div className={classes.processHeader}>
              <p>作業時間</p>
              {user.initialParameters.isShowSlipNumber && (
                <div className={classes.name}>
                  <p>予定</p>
                  <p>/</p>
                  <p>合計</p>
                  <p>/</p>
                  <p>個人</p>
                </div>
              )}
            </div>
            {filteredProcessList.map((process, index) => {
              const isOdd = index % 2 === 1 // 奇数のインデックス
              const textStyle = {
                color: !isOdd ? '#feaac9' : 'inherit',
              }

              // summaryByWorkerのachivementByProcessから一致する工程を探す
              const matchingWorkerProcess =
                summaryByWorker.achivementByProcess.find(
                  (p) => Number(p.processId) === Number(process.processId)
                )

              // summaryByProductNoのachivementByProcessから一致する工程を探す
              const matchingProductProcess =
                summaryByProductNo.achivementByProcess.find(
                  (p) => Number(p.processId) === Number(process.processId)
                )

              // 実績値を取得（存在しない場合は0）
              const workerAchievement = matchingWorkerProcess
                ? Number(matchingWorkerProcess.achievementsTimeByHour).toFixed(
                    1
                  )
                : '0'

              const productAchievement = matchingProductProcess
                ? Number(matchingProductProcess.achievementsTimeByHour).toFixed(
                    1
                  )
                : '0'

              return (
                <div className={classes.process} key={process.processId}>
                  {user.initialParameters.isShowSlipNumber ? (
                    <>
                      <div>
                        <div style={textStyle}>{process.processName}</div>
                        <div style={textStyle}>{process.slipNumber}</div>
                      </div>
                      <div style={textStyle} className={classes.hours}>
                        {/* ここでestimatedTimeと実績値を表示 */}
                        <div>{Number(process.estimatedTime).toFixed(1)}</div>
                        <div>{'/'}</div>
                        <div>{Number(productAchievement).toFixed(1)}</div>
                        <div>{'/'}</div>
                        <div>{Number(workerAchievement).toFixed(1)}</div>
                      </div>
                    </>
                  ) : (
                    <div style={{ minWidth: '136px', ...textStyle }}>
                      {process.processName}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <div
            className={classes.contents}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            ref={isPinned ? scrollRef : undefined}
            style={{
              maxWidth: contentWidth(),
            }}
          >
            <div ref={targetRef}></div>

            {calendar.map((date: Calendar, index) => (
              <div
                key={index}
                className={classes.row}
                style={{
                  backgroundColor: date.isHoliday ? '#FEE5D8' : '#f8f7d0',
                }}
              >
                <div
                  className={classes.summary}
                  style={{
                    backgroundColor: theme.palette.backColor.dark,
                    color: date.isHoliday
                      ? theme.palette.foreColor.holiday
                      : theme.palette.foreColor.light,
                  }}
                >
                  <div
                    className={classes.edit}
                    onClick={() => handleEditClick(date.date)}
                  >
                    <IconContext.Provider
                      value={{
                        color: theme.palette.foreColor.main,
                        size: '18px',
                      }}
                    >
                      <MdModeEdit />
                    </IconContext.Provider>
                  </div>
                  <div className={classes.date}>{date.shortDate}</div>
                  <div>
                    {
                      createOnedayDiary(
                        date.date,
                        filteredDiaryList,
                        filteredProcessList
                      )?.startTime
                    }
                  </div>
                  <div>
                    {
                      createOnedayDiary(
                        date.date,
                        filteredDiaryList,
                        filteredProcessList
                      )?.endTime
                    }
                  </div>
                  <div className={classes.text}>
                    {createOnedayDiary(
                      date.date,
                      filteredDiaryList,
                      filteredProcessList
                    )?.breakTime
                      ? createOnedayDiary(
                          date.date,
                          filteredDiaryList,
                          filteredProcessList
                        )?.breakTime.toFixed(1)
                      : ''}
                  </div>
                  <div className={classes.number}>
                    {createOnedayDiary(
                      date.date,
                      filteredDiaryList,
                      filteredProcessList
                    )?.totalDirectLaborByHour.toFixed(1)}
                  </div>
                  <div className={classes.number}>
                    {createOnedayDiary(
                      date.date,
                      filteredDiaryList,
                      filteredProcessList
                    )?.totalWorkingTimeByHour.toFixed(1)}
                  </div>
                </div>
                <div className={classes.detail}>
                  {filteredProcessList.map((process) => {
                    const filteredDiaries = filteredDiaryList.filter(
                      (diary) =>
                        diary.workingDate === date.date &&
                        diary.processId === process.processId
                    )

                    const totalWorkingTime = filteredDiaries.reduce(
                      (total, diary) => total + diary.workingTimeByHour,
                      0
                    )
                    const isDisabled = filteredDiaries.every(
                      (diary) => diary.isDisabled
                    )
                    const comments = filteredDiaries
                      .map((diary) => diary.comment.trim())
                      .filter((comment) => comment.length > 0)
                      .join('\n')

                    return (
                      <div
                        onClick={() => handleClickCells(comments)}
                        key={process.processId}
                        className={classes.number}
                        style={createCellStyle(
                          isDisabled,
                          totalWorkingTime,
                          comments
                        )}
                      >
                        {totalWorkingTime > 0
                          ? totalWorkingTime.toFixed(1)
                          : ''}
                        {/* {comments && (
                          <div className={classes.comment}>{comments}</div>
                        )} */}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
            <ScrollButtonHorizontal
              targetElm={scrollRef}
              scrollElm={scrollRef}
              distance={50}
            />
          </div>
        </div>
        {isShowCommentModal && (
          <ViewComment
            setIsShow={setIsShowCommentModal}
            isReadOnly={true}
            comment={comment}
          />
        )}
        {isShowSingleInputModal && (
          <SingleInputModal
            setIsShow={setIsShowSingleInputModal}
            dateString={dateString}
            workerId={workerId}
            callFrom={'matrix'}
            initialProductNo={summaryByWorker.productNo}
            initialProductBno={summaryByWorker.productBno}
          />
        )}
      </div>
    )
  }
)

export default TableMainForMatrix
